import { z } from 'zod';
import {
  Email,
  IsoDate,
  IsoDateTime,
  NonEmptyName,
  Pence,
  Phone,
  Slug,
  Uuid,
} from '../primitives.js';

/**
 * WS8 — public online-booking contract (book.salonomy.com/<salon>). Unauthenticated, no account: a
 * stranger books a real appointment in under a minute. The non-negotiables this contract encodes:
 *
 *  • No account required — identity is one verification code (email/phone), never a password.
 *  • Terms shown == terms enforced — the policy text returned with a HOLD is rendered from the same
 *    frozen policy_version the created appointment will carry (brief 07 honesty invariant).
 *  • Leak nothing across tenants / sealed books — every response here is customer-facing only: no
 *    renterId, no clientId, no other client's name, no internal staff/renter graph.
 *  • Slot-squatting fails — a booking is a two-step HOLD → CONFIRM: the hold reserves the slot with
 *    an expiry, and an unverified hold never becomes an appointment.
 *  • Double-booking impossible — confirmation goes through the SAME assertNoClash + appointment
 *    create path as a staff booking, so the public flow cannot double-book by construction.
 *
 * Money is integer pence; dates are ISO. The shapes mirror the existing /public + /q contract idiom.
 */

// ── Online-bookable services for a salon page ────────────────────────────────────

/**
 * One online-bookable service as a public booking page sees it: enough to choose and to show the
 * up-front money + patch-test note. priceFrom is the "from" price (variants are priced at booking).
 */
export const PublicBookableService = z
  .object({
    id: Uuid,
    name: z.string(),
    category: z.string().nullable(),
    durationMin: z.int(),
    priceFrom: Pence,
    /** The service needs a valid patch test (the page warns + the booking is flagged, never blocked). */
    requiresPatchTest: z.boolean(),
    /** Plain-English deposit/cancellation/no-show terms for THIS service (no asterisks). */
    policyText: z.string(),
    depositText: z.string(),
    cancellationText: z.string(),
    noShowText: z.string(),
  })
  .meta({ id: 'PublicBookableService' });
export type PublicBookableService = z.infer<typeof PublicBookableService>;

/** The bookable-services payload for a salon slug (name + online-booking window + the services). */
export const PublicBookableServices = z
  .object({
    salonName: z.string(),
    salonSlug: Slug,
    timezone: z.string(),
    onlineBookingEnabled: z.boolean(),
    /** Earliest a public client may book, in minutes from now (the salon's lead time). */
    leadMinutes: z.int().min(0),
    /** How far ahead a public client may book, in days. */
    windowDays: z.int().min(1),
    services: z.array(PublicBookableService),
  })
  .meta({ id: 'PublicBookableServices' });
export type PublicBookableServices = z.infer<typeof PublicBookableServices>;

// ── Public availability search ───────────────────────────────────────────────────

/**
 * Public availability query: a service + a date (the salon's slug is the path param). An optional
 * staffId narrows to one named professional; omitted means "anyone". granularityMin matches the
 * authenticated availability route's bucketing.
 */
export const PublicAvailabilityQuery = z.object({
  serviceId: Uuid,
  date: IsoDate,
  staffId: Uuid.optional(),
  granularityMin: z.coerce.number().int().min(5).max(120).default(15),
});
export type PublicAvailabilityQuery = z.infer<typeof PublicAvailabilityQuery>;

/** A single public slot — staff display NAME only (never any internal id graph beyond staffId). */
export const PublicSlot = z.object({
  staffId: Uuid,
  staffName: z.string(),
  startAt: IsoDateTime,
  endAt: IsoDateTime,
});
export type PublicSlot = z.infer<typeof PublicSlot>;

export const PublicAvailabilityResult = z
  .object({
    serviceId: Uuid,
    date: IsoDate,
    slots: z.array(PublicSlot),
  })
  .meta({ id: 'PublicAvailabilityResult' });
export type PublicAvailabilityResult = z.infer<typeof PublicAvailabilityResult>;

// ── Step 1: start a booking (place a verified hold on a slot) ─────────────────────

/**
 * Start a booking: the stranger's lightweight identity (name + email OR phone) and the chosen slot.
 * No password, no account. This RESERVES the slot as a hold and issues a verification code.
 */
export const StartPublicBooking = z
  .object({
    name: NonEmptyName,
    email: Email.optional(),
    phone: Phone.optional(),
    serviceId: Uuid,
    /**
     * Optional pricing variant of the service (short/medium/long). When set it rides the hold: the
     * concrete price is resolved + FROZEN on the hold at /start, so the price shown at hold equals the
     * price charged at confirm. Omitted → the service's derived "from £X" price (unchanged behaviour).
     */
    variantId: Uuid.optional(),
    staffId: Uuid,
    startAt: IsoDateTime,
    notes: z.string().max(2000).optional(),
  })
  .refine((b) => Boolean(b.email || b.phone), {
    message: 'Provide an email or a phone number so we can confirm your booking',
    path: ['email'],
  })
  .meta({ id: 'StartPublicBooking' });
export type StartPublicBooking = z.infer<typeof StartPublicBooking>;

/**
 * The hold the server placed: an opaque holdId, when it expires (the squat-proof window), the terms
 * the client must have seen (rendered from the frozen policy version), and whether a deposit is due.
 * In NON-production (no live SMS/email gateway) the dev verification code is surfaced here so the
 * flow is testable end-to-end; it is omitted in production.
 */
export const PublicBookingHold = z
  .object({
    holdId: Uuid,
    expiresAt: IsoDateTime,
    serviceId: Uuid,
    staffId: Uuid,
    staffName: z.string(),
    startAt: IsoDateTime,
    endAt: IsoDateTime,
    pricePence: Pence,
    depositRequired: z.boolean(),
    depositAmountPence: Pence,
    requiresPatchTest: z.boolean(),
    policyText: z
      .string()
      .describe('the exact terms that will be enforced — shown before commitment'),
    /** Dev-only echo of the verification code (never present in production). */
    devCode: z.string().nullable().optional(),
  })
  .meta({ id: 'PublicBookingHold' });
export type PublicBookingHold = z.infer<typeof PublicBookingHold>;

// ── Step 2: confirm with the verification code (creates the appointment) ──────────

export const ConfirmPublicBooking = z
  .object({
    holdId: Uuid,
    code: z.string().min(4).max(12),
  })
  .meta({ id: 'ConfirmPublicBooking' });
export type ConfirmPublicBooking = z.infer<typeof ConfirmPublicBooking>;

/**
 * The confirmed booking. Carries the signed MANAGE token (the no-login capability for
 * reschedule/cancel), enough to render an ICS + confirmation, the terms that were enforced, and —
 * when a deposit was due and Stripe is connected — the client_secret to collect it (returned
 * immediately = optimistic confirmation, never a stuck spinner).
 */
export const PublicBookingConfirmation = z
  .object({
    appointmentId: Uuid,
    manageToken: z.string().describe('signed no-login token for the manage-booking link'),
    salonName: z.string(),
    salonSlug: Slug,
    serviceName: z.string(),
    staffName: z.string(),
    startAt: IsoDateTime,
    endAt: IsoDateTime,
    pricePence: Pence,
    status: z.enum(['booked', 'confirmed']),
    requiresPatchTest: z.boolean(),
    policyText: z.string(),
    deposit: z
      .object({
        amountPence: Pence,
        /** Present only when a deposit is due AND payments are connected. */
        clientSecret: z.string().nullable(),
        accountId: z.string().nullable(),
      })
      .nullable(),
  })
  .meta({ id: 'PublicBookingConfirmation' });
export type PublicBookingConfirmation = z.infer<typeof PublicBookingConfirmation>;

// ── Manage booking (signed token, no login) ──────────────────────────────────────

/**
 * What the manage-booking page shows: the booking, whether self-service reschedule/cancel are still
 * allowed (inside the salon's policy window), and the policy text to display when they are not.
 */
export const ManageBookingView = z
  .object({
    appointmentId: Uuid,
    salonName: z.string(),
    salonSlug: Slug,
    serviceName: z.string(),
    staffName: z.string(),
    startAt: IsoDateTime,
    endAt: IsoDateTime,
    status: z.string(),
    /** True while the client may still cancel free / reschedule themselves (policy free window). */
    canSelfManage: z.boolean(),
    policyText: z.string(),
    /** What cancelling NOW would cost (0 inside the free window). */
    cancellationFeePence: Pence,
  })
  .meta({ id: 'ManageBookingView' });
export type ManageBookingView = z.infer<typeof ManageBookingView>;

/** Reschedule a booking to a new slot (must be within the policy self-service window). */
export const RescheduleBooking = z
  .object({
    startAt: IsoDateTime,
    /** Optional: move to a different professional offered by the availability search. */
    staffId: Uuid.optional(),
  })
  .meta({ id: 'RescheduleBooking' });
export type RescheduleBooking = z.infer<typeof RescheduleBooking>;

/** The result of a manage action (reschedule / cancel): the booking's new state. */
export const ManageBookingResult = z
  .object({
    appointmentId: Uuid,
    status: z.string(),
    startAt: IsoDateTime,
    endAt: IsoDateTime,
    staffName: z.string(),
  })
  .meta({ id: 'ManageBookingResult' });
export type ManageBookingResult = z.infer<typeof ManageBookingResult>;
